<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Tipos de planilla</h1>
    </div>

    <wz-table-container>

      <wz-table-toolbar>
        <template #left>
          <wz-filter-menu
            :options="filterOptions"
            :selected-option-filter="filters"
            @setFilters="setFilters"
          />
        </template>
        <template #right>
          <wz-tooltip text="Recargar">
            <wz-button variant="outline" @click="loadData">
              <i class="ri-loop-right-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-button variant="primary" @click="$router.push({ name: 'payroll-type-create' })">Agregar</wz-button>
        </template>
        <template #filters v-if="showFiltersList">
          <wz-badge
            v-for="(val, name) in filterList"
            :key="name"
            variant="primary"
            pill
            dismissible
            @dismiss="deleteFilter(name)"
          >
            {{ getFilterName(name, val) }}
          </wz-badge>
        </template>
      </wz-table-toolbar>

      <wz-table>
        <thead>
          <tr>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'code' }" @click="toggleSort('code')">
              Código <i :class="['wz-th-sort-icon', sortIcon('code')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'name' }" @click="toggleSort('name')">
              Nombre <i :class="['wz-th-sort-icon', sortIcon('name')]"></i>
            </th>
            <th>Frecuencia</th>
            <th>Categoría</th>
            <th style="text-align: center">BS</th>
            <th style="text-align: center">AF</th>
            <th style="text-align: center">Pensión</th>
            <th style="text-align: center">EsSalud</th>
            <th style="text-align: center">IR</th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'status' }" @click="toggleSort('status')">
              Estado <i :class="['wz-th-sort-icon', sortIcon('status')]"></i>
            </th>
            <th>Opciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="sortedList(list).length === 0">
            <td colspan="11" class="text-center text-muted py-4">No se encontraron resultados.</td>
          </tr>
          <tr v-for="o in sortedList(list)" :key="o.id">
            <td>{{ o.code }}</td>
            <td>{{ o.name }}</td>
            <td>
              <wz-badge :variant="frequencyVariant(o.frequency)" pill>{{ getFrequencyLabel(o.frequency) }}</wz-badge>
            </td>
            <td>
              <wz-badge :variant="o.taxCategory === 'FIFTH' ? 'info-solid' : 'secondary-solid'" pill>
                {{ o.taxCategory === 'FIFTH' ? '5ta' : '4ta' }}
              </wz-badge>
            </td>
            <td style="text-align: center"><i :class="o.affectsSocialBenefits  ? 'ri-check-line text-success' : 'ri-close-line text-muted'"></i></td>
            <td style="text-align: center"><i :class="o.includesFamilyAllowance ? 'ri-check-line text-success' : 'ri-close-line text-muted'"></i></td>
            <td style="text-align: center"><i :class="o.includesPension         ? 'ri-check-line text-success' : 'ri-close-line text-muted'"></i></td>
            <td style="text-align: center"><i :class="o.includesEssalud         ? 'ri-check-line text-success' : 'ri-close-line text-muted'"></i></td>
            <td style="text-align: center"><i :class="o.includesIr              ? 'ri-check-line text-success' : 'ri-close-line text-muted'"></i></td>
            <td>
              <wz-badge :variant="statusVariant(o.status)" pill>{{ getStatus(o.status).label }}</wz-badge>
            </td>
            <td style="text-align: center">
              <wz-dropdown
                :items="[
                  { key: 'edit',   label: 'Editar' },
                  { key: 'status', label: getStatus(o.status).action },
                ]"
                placement="left"
                @click="handleAction(o, $event)"
              />
            </td>
          </tr>
        </tbody>
      </wz-table>

      <wz-table-footer>
        <span>{{ list.length }} registro(s)</span>
      </wz-table-footer>

    </wz-table-container>

    <wz-confirm-modal
      v-model="confirmModal.show"
      :title="confirmModal.title"
      :description="confirmModal.description"
      :accept-text="confirmModal.acceptText"
      :cancel-text="confirmModal.cancelText"
      @confirm="onModalConfirm"
      @cancel="closeConfirmModal"
    />
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { Constants as LocalConstants } from '@/core/utils'
import { FilterMixin } from "@wizepe/wizeui"
import { SortMixin, ConfirmModalMixin } from '@wizepe/wizeui'
import { PayrollTypeService } from '@/core/services'

export default {
  mixins: [SortMixin, FilterMixin, ConfirmModalMixin],
  data() {
    return {
      list: [],
      filterOptions: [
        {
          label: 'Estado',
          key: 'status',
          type: LocalConstants.TypeFilters.SINGLE,
          options: [LocalConstants.PayrollTypeStatus.ACTIVE, LocalConstants.PayrollTypeStatus.INACTIVE],
        },
      ],
    }
  },
  methods: {
    statusVariant(status) {
      return this.getStatus(status)?.color || 'dark'
    },
    frequencyVariant(freq) {
      const map = { WEEKLY: 'warning-solid', BIWEEKLY: 'info-solid', MONTHLY: 'success-solid' }
      return map[freq] || 'secondary-solid'
    },
    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const resp = await PayrollTypeService.list()
        let items = resp.payload || []
        // Filtro Estado en cliente: la key del filtro es string ('ACTIVO'/'SUSPENDIDO'),
        // el status del registro es numérico (1/0).
        const s = this.filters.status
        if (s === LocalConstants.PayrollTypeStatus.ACTIVE.key) {
          items = items.filter(o => Number(o.status) === 1)
        } else if (s === LocalConstants.PayrollTypeStatus.INACTIVE.key) {
          items = items.filter(o => Number(o.status) === 0)
        }
        this.list = items
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar tipos de planilla')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    // status del registro es numérico (1/0); mapea al objeto de estado para label/color/acción.
    getStatus(status) {
      return Number(status) === 1
        ? LocalConstants.PayrollTypeStatus.ACTIVE
        : LocalConstants.PayrollTypeStatus.INACTIVE
    },
    getFrequencyLabel(freq) {
      const map = { WEEKLY: 'Semanal', BIWEEKLY: 'Quincenal', MONTHLY: 'Mensual' }
      return map[freq] || freq
    },
    handleAction(o, item) {
      if (item.key === 'edit')   this.$router.push({ name: 'payroll-type-edit', params: { id: o.id } })
      if (item.key === 'status') this.openChangeStatus(o)
    },
    openChangeStatus(o) {
      this.selectedOption = o
      this.openConfirmModal({
        title:       'Cambiar estado',
        description: '¿Está seguro que desea cambiar el estado?',
        acceptText:  'Aceptar',
        action:      'update',
      })
    },
    onModalConfirm() {
      const action = this.confirmModal.action
      this.closeConfirmModal()
      if (action === 'update') this.changeStatus()
    },
    async changeStatus() {
      try {
        await this.$store.dispatch('app/loading', true)
        // Toggle sobre el status numérico real (1/0) que espera el backend (@RequestParam Integer).
        const status = Number(this.selectedOption.status) === 1 ? 0 : 1
        await PayrollTypeService.changeStatus(this.selectedOption.id, status)
        await this.loadData()
        Alert.success('Estado actualizado correctamente')
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al cambiar estado')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
  },
  async created() { await this.loadData() },
}
</script>

<style lang="stylus"></style>
